<template>
  <v-dialog
    :value="isOpen"
    max-width="630px"
    class="highestZIndex"
    @click:outside="closeDialog"
  >
    <v-card class="effects-card">
      <v-card-title class="effects-header">
        <v-btn icon @click="resetToDefault" title="Reset All">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <span class="effects-title">EFFECTS</span>
        <v-btn icon @click="closeDialog" title="Close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="effects-content">
        <!-- COMPRESSOR SECTION -->
        <div class="compressor-header section-header">
          <div class="section-title-row">
            <v-btn icon x-small @click="resetCompressor" :disabled="!compressorEnabled">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
            <v-subheader class="text-h6 pa-0 section-label">
              COMPRESSOR
            </v-subheader>
            <v-switch
              v-model="compressorEnabled"
              hide-details
              dense
              class="section-switch"
            ></v-switch>
          </div>
          <div class="header-controls">
            <span class="auto-makeup-label">Auto Makeup Gain</span>
            <v-switch
              v-model="autoMakeupGain"
              hide-details
              dense
              class="auto-makeup-switch"
              :disabled="!compressorEnabled"
              title="Auto Makeup Gain"
            ></v-switch>
          </div>
        </div>

        <div class="section-content" :class="{ 'section-disabled': !compressorEnabled }">
          <v-divider class="mb-3"></v-divider>

          <!-- Gain Reduction Meter. Desktop reads the live compressor node;
               iOS shows an estimated envelope computed during the offline
               render (see grEnvelope.js). -->
          <div class="gain-reduction-meter-container">
            <div class="meter-label">GR</div>
            <div class="meter-track">
              <div
                class="meter-fill"
                :style="{ width: gainReductionPercent + '%' }"
              ></div>
              <div class="meter-scale">
                <span class="meter-mark" style="left: 0%">-20</span>
                <span class="meter-mark" style="left: 25%">-15</span>
                <span class="meter-mark" style="left: 50%">-10</span>
                <span class="meter-mark" style="left: 75%">-5</span>
                <span class="meter-mark" style="left: 100%">0</span>
              </div>
            </div>
            <div class="meter-value">{{ gainReductionDisplay }} dB</div>
          </div>
          <div v-if="showOfflineFxNote" class="offline-fx-note">
            Changes take a moment to apply on iPhone/iPad
          </div>

          <div class="slider-row">
            <span class="slider-label">Threshold:</span>
            <v-slider
              v-model="compressor.threshold"
              :min="-100"
              :max="0"
              :step="0.5"
              :disabled="!compressorEnabled"
              hide-details
              color="secondary"
              track-color="tertiary"
              @input="updateCompressorThreshold"
            >
              <template v-slot:append>
                <span class="slider-value">{{ compressor.threshold }} dB</span>
              </template>
            </v-slider>
          </div>

          <div class="slider-row">
            <span class="slider-label">Ratio:</span>
            <v-slider
              v-model="compressor.ratio"
              :min="1"
              :max="20"
              :step="0.1"
              :disabled="!compressorEnabled"
              hide-details
              color="secondary"
              track-color="tertiary"
              @input="updateCompressorRatio"
            >
              <template v-slot:append>
                <span class="slider-value">{{ compressor.ratio.toFixed(1) }}:1</span>
              </template>
            </v-slider>
          </div>

          <div class="slider-row">
            <span class="slider-label">Attack:</span>
            <v-slider
              v-model="compressor.attack"
              :min="0"
              :max="300"
              :step="1"
              :disabled="!compressorEnabled"
              hide-details
              color="secondary"
              track-color="tertiary"
              @input="updateCompressorAttack"
            >
              <template v-slot:append>
                <span class="slider-value">{{ compressor.attack }} ms</span>
              </template>
            </v-slider>
          </div>

          <div class="slider-row">
            <span class="slider-label">Release:</span>
            <v-slider
              v-model="compressor.release"
              :min="0"
              :max="1500"
              :step="10"
              :disabled="!compressorEnabled"
              hide-details
              color="secondary"
              track-color="tertiary"
              @input="updateCompressorRelease"
            >
              <template v-slot:append>
                <span class="slider-value">{{ compressor.release }} ms</span>
              </template>
            </v-slider>
          </div>

          <div class="slider-row">
            <span class="slider-label">Makeup:</span>
            <v-slider
              v-model="compressor.makeupGain"
              :min="-12"
              :max="12"
              :step="0.1"
              :disabled="!compressorEnabled || autoMakeupGain"
              hide-details
              color="secondary"
              track-color="tertiary"
              @input="updateCompressorMakeup"
            >
              <template v-slot:append>
                <span class="slider-value">{{ formatGain(compressor.makeupGain) }} dB</span>
              </template>
            </v-slider>
          </div>
        </div>

        <!-- EQUALIZER SECTION -->
        <div class="eq-header section-header">
          <div class="section-title-row">
            <v-btn icon x-small @click="resetEqualizer" :disabled="!eqEnabled">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
            <v-subheader class="text-h6 pa-0 section-label">
              EQUALIZER
            </v-subheader>
            <v-switch
              v-model="eqEnabled"
              hide-details
              dense
              class="section-switch"
            ></v-switch>
          </div>
        </div>

        <div class="section-content" :class="{ 'section-disabled': !eqEnabled }">
          <!-- High Shelf -->
          <v-divider class="mb-3"></v-divider>
          <div class="eq-band mb-4">
            <div class="band-title-row mb-2">
              <div class="band-title">High Shelf ({{ highShelf.frequency }} Hz)</div>
              <v-btn icon x-small @click="resetHighShelf" :disabled="!eqEnabled">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <div class="slider-row">
              <span class="slider-label">Freq:</span>
              <v-slider
                v-model="highShelf.frequency"
                :min="1000"
                :max="20000"
                :step="10"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateHighShelfFreq"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ highShelf.frequency }} Hz</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Gain:</span>
              <v-slider
                v-model="highShelf.gain"
                :min="-12"
                :max="12"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateHighShelfGain"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ formatGain(highShelf.gain) }} dB</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Q:</span>
              <v-slider
                v-model="highShelf.q"
                :min="0.1"
                :max="10"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateHighShelfQ"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ highShelf.q.toFixed(2) }}</span>
                </template>
              </v-slider>
            </div>
          </div>

          <!-- Mid 3 (4 kHz) -->
          <v-divider class="mb-3"></v-divider>
          <div class="eq-band mb-4">
            <div class="band-title-row mb-2">
              <div class="band-title">Mid 3 ({{ mid3.frequency }} Hz)</div>
              <v-btn icon x-small @click="resetMid3" :disabled="!eqEnabled">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <div class="slider-row">
              <span class="slider-label">Freq:</span>
              <v-slider
                v-model="mid3.frequency"
                :min="500"
                :max="10000"
                :step="10"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid3Freq"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ mid3.frequency }} Hz</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Gain:</span>
              <v-slider
                v-model="mid3.gain"
                :min="-12"
                :max="12"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid3Gain"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ formatGain(mid3.gain) }} dB</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Q:</span>
              <v-slider
                v-model="mid3.q"
                :min="0.1"
                :max="10"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid3Q"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ mid3.q.toFixed(2) }}</span>
                </template>
              </v-slider>
            </div>
          </div>

          <!-- Mid 2 (1 kHz) -->
          <v-divider class="mb-3"></v-divider>
          <div class="eq-band mb-4">
            <div class="band-title-row mb-2">
              <div class="band-title">Mid 2 ({{ mid2.frequency }} Hz)</div>
              <v-btn icon x-small @click="resetMid2" :disabled="!eqEnabled">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <div class="slider-row">
              <span class="slider-label">Freq:</span>
              <v-slider
                v-model="mid2.frequency"
                :min="200"
                :max="5000"
                :step="10"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid2Freq"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ mid2.frequency }} Hz</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Gain:</span>
              <v-slider
                v-model="mid2.gain"
                :min="-12"
                :max="12"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid2Gain"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ formatGain(mid2.gain) }} dB</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Q:</span>
              <v-slider
                v-model="mid2.q"
                :min="0.1"
                :max="10"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid2Q"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ mid2.q.toFixed(2) }}</span>
                </template>
              </v-slider>
            </div>
          </div>

          <!-- Mid 1 (250 Hz) -->
          <v-divider class="mb-3"></v-divider>
          <div class="eq-band mb-4">
            <div class="band-title-row mb-2">
              <div class="band-title">Mid 1 ({{ mid1.frequency }} Hz)</div>
              <v-btn icon x-small @click="resetMid1" :disabled="!eqEnabled">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <div class="slider-row">
              <span class="slider-label">Freq:</span>
              <v-slider
                v-model="mid1.frequency"
                :min="50"
                :max="2000"
                :step="5"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid1Freq"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ mid1.frequency }} Hz</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Gain:</span>
              <v-slider
                v-model="mid1.gain"
                :min="-12"
                :max="12"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid1Gain"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ formatGain(mid1.gain) }} dB</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Q:</span>
              <v-slider
                v-model="mid1.q"
                :min="0.1"
                :max="10"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateMid1Q"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ mid1.q.toFixed(2) }}</span>
                </template>
              </v-slider>
            </div>
          </div>

          <!-- Low Shelf -->
          <v-divider class="mb-3"></v-divider>
          <div class="eq-band mb-4">
            <div class="band-title-row mb-2">
              <div class="band-title">Low Shelf ({{ lowShelf.frequency }} Hz)</div>
              <v-btn icon x-small @click="resetLowShelf" :disabled="!eqEnabled">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <div class="slider-row">
              <span class="slider-label">Freq:</span>
              <v-slider
                v-model="lowShelf.frequency"
                :min="20"
                :max="500"
                :step="5"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateLowShelfFreq"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ lowShelf.frequency }} Hz</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Gain:</span>
              <v-slider
                v-model="lowShelf.gain"
                :min="-12"
                :max="12"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateLowShelfGain"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ formatGain(lowShelf.gain) }} dB</span>
                </template>
              </v-slider>
            </div>

            <div class="slider-row">
              <span class="slider-label">Q:</span>
              <v-slider
                v-model="lowShelf.q"
                :min="0.1"
                :max="10"
                :step="0.1"
                :disabled="!eqEnabled"
                hide-details
                color="secondary"
                track-color="tertiary"
                @input="updateLowShelfQ"
              >
                <template v-slot:append>
                  <span class="slider-value">{{ lowShelf.q.toFixed(2) }}</span>
                </template>
              </v-slider>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AudioService from '@/services/AudioService';

export default {
  name: 'AudioEffectsDialog',

  data() {
    return {
      // Local copies of EQ settings
      highShelf: { frequency: 8000, gain: 0, q: 0.7 },
      mid3: { frequency: 4000, gain: 0, q: 1.0 },
      mid2: { frequency: 1000, gain: 0, q: 1.0 },
      mid1: { frequency: 250, gain: 0, q: 1.0 },
      lowShelf: { frequency: 80, gain: 0, q: 0.7 },

      // Local copy of compressor settings
      compressor: {
        threshold: -24,
        ratio: 3.5,
        attack: 30,
        release: 100,
        makeupGain: 0
      },

      // Gain reduction meter
      gainReduction: 0,
      meterAnimationId: null
    };
  },

  computed: {
    ...mapGetters({
      isOpen: 'audioEffects/isAudioEffectsDialogOpen',
      storeEqSettings: 'audioEffects/eqSettings',
      storeCompressorSettings: 'audioEffects/compressorSettings',
      storeCompressorEnabled: 'audioEffects/isCompressorEnabled',
      storeEqEnabled: 'audioEffects/isEqEnabled',
      storeAutoMakeupGain: 'audioEffects/isAutoMakeupGainEnabled'
    }),

    compressorEnabled: {
      get() {
        return this.storeCompressorEnabled;
      },
      set(value) {
        this.$store.dispatch('audioEffects/setCompressorEnabled', value);
        AudioService.setCompressorEnabled(value);
      }
    },

    eqEnabled: {
      get() {
        return this.storeEqEnabled;
      },
      set(value) {
        this.$store.dispatch('audioEffects/setEqEnabled', value);
        AudioService.setEqEnabled(value);
      }
    },

    autoMakeupGain: {
      get() {
        return this.storeAutoMakeupGain;
      },
      set(value) {
        this.$store.dispatch('audioEffects/setAutoMakeupGain', value);
        // Reset makeup gain to 0 when turning auto makeup on
        if (value) {
          this.compressor.makeupGain = 0;
          this.updateCompressorParam({ param: 'makeupGain', value: 0 });
        }
        AudioService.updateAutoMakeupGain();
      }
    },

    gainReductionPercent() {
      // Convert dB (-20 to 0) to percentage (100 to 0)
      // More negative = more compression = wider bar (fills from right)
      const clamped = Math.max(-20, Math.min(0, this.gainReduction));
      return Math.abs(clamped) / 20 * 100;
    },

    gainReductionDisplay() {
      return this.gainReduction.toFixed(1);
    },

    showOfflineFxNote() {
      return !AudioService.isCompressorMeterLive();
    }
  },

  watch: {
    isOpen(newVal) {
      if (newVal) {
        // Dialog opened - load current settings
        // Note: Web Audio API is already initialized when tracks load,
        // so we don't need to initialize it here. Just load the UI settings.
        this.loadSettings();
        this.startMeterAnimation();
      } else {
        this.stopMeterAnimation();
      }
    }
  },

  methods: {
    ...mapActions({
      closeDialogAction: 'audioEffects/closeDialog',
      updateEqBand: 'audioEffects/updateEqBand',
      updateCompressorParam: 'audioEffects/updateCompressorParam',
      resetToDefaultAction: 'audioEffects/resetToDefault'
    }),

    loadSettings() {
      // Load EQ settings from store
      this.highShelf = { ...this.storeEqSettings.highShelf };
      this.mid3 = { ...this.storeEqSettings.mid3 };
      this.mid2 = { ...this.storeEqSettings.mid2 };
      this.mid1 = { ...this.storeEqSettings.mid1 };
      this.lowShelf = { ...this.storeEqSettings.lowShelf };

      // Load compressor settings from store
      this.compressor = { ...this.storeCompressorSettings };
    },

    closeDialog() {
      this.closeDialogAction();
    },

    formatGain(value) {
      return value >= 0 ? `+${value.toFixed(1)}` : value.toFixed(1);
    },

    // High Shelf updates
    updateHighShelfFreq() {
      this.updateEqBand({ band: 'highShelf', property: 'frequency', value: this.highShelf.frequency });
      AudioService.updateEqBand(0, this.highShelf.frequency, this.highShelf.gain, this.highShelf.q);
    },
    updateHighShelfGain() {
      this.updateEqBand({ band: 'highShelf', property: 'gain', value: this.highShelf.gain });
      AudioService.updateEqBand(0, this.highShelf.frequency, this.highShelf.gain, this.highShelf.q);
    },
    updateHighShelfQ() {
      this.updateEqBand({ band: 'highShelf', property: 'q', value: this.highShelf.q });
      AudioService.updateEqBand(0, this.highShelf.frequency, this.highShelf.gain, this.highShelf.q);
    },

    // Mid 3 updates
    updateMid3Freq() {
      this.updateEqBand({ band: 'mid3', property: 'frequency', value: this.mid3.frequency });
      AudioService.updateEqBand(1, this.mid3.frequency, this.mid3.gain, this.mid3.q);
    },
    updateMid3Gain() {
      this.updateEqBand({ band: 'mid3', property: 'gain', value: this.mid3.gain });
      AudioService.updateEqBand(1, this.mid3.frequency, this.mid3.gain, this.mid3.q);
    },
    updateMid3Q() {
      this.updateEqBand({ band: 'mid3', property: 'q', value: this.mid3.q });
      AudioService.updateEqBand(1, this.mid3.frequency, this.mid3.gain, this.mid3.q);
    },

    // Mid 2 updates
    updateMid2Freq() {
      this.updateEqBand({ band: 'mid2', property: 'frequency', value: this.mid2.frequency });
      AudioService.updateEqBand(2, this.mid2.frequency, this.mid2.gain, this.mid2.q);
    },
    updateMid2Gain() {
      this.updateEqBand({ band: 'mid2', property: 'gain', value: this.mid2.gain });
      AudioService.updateEqBand(2, this.mid2.frequency, this.mid2.gain, this.mid2.q);
    },
    updateMid2Q() {
      this.updateEqBand({ band: 'mid2', property: 'q', value: this.mid2.q });
      AudioService.updateEqBand(2, this.mid2.frequency, this.mid2.gain, this.mid2.q);
    },

    // Mid 1 updates
    updateMid1Freq() {
      this.updateEqBand({ band: 'mid1', property: 'frequency', value: this.mid1.frequency });
      AudioService.updateEqBand(3, this.mid1.frequency, this.mid1.gain, this.mid1.q);
    },
    updateMid1Gain() {
      this.updateEqBand({ band: 'mid1', property: 'gain', value: this.mid1.gain });
      AudioService.updateEqBand(3, this.mid1.frequency, this.mid1.gain, this.mid1.q);
    },
    updateMid1Q() {
      this.updateEqBand({ band: 'mid1', property: 'q', value: this.mid1.q });
      AudioService.updateEqBand(3, this.mid1.frequency, this.mid1.gain, this.mid1.q);
    },

    // Low Shelf updates
    updateLowShelfFreq() {
      this.updateEqBand({ band: 'lowShelf', property: 'frequency', value: this.lowShelf.frequency });
      AudioService.updateEqBand(4, this.lowShelf.frequency, this.lowShelf.gain, this.lowShelf.q);
    },
    updateLowShelfGain() {
      this.updateEqBand({ band: 'lowShelf', property: 'gain', value: this.lowShelf.gain });
      AudioService.updateEqBand(4, this.lowShelf.frequency, this.lowShelf.gain, this.lowShelf.q);
    },
    updateLowShelfQ() {
      this.updateEqBand({ band: 'lowShelf', property: 'q', value: this.lowShelf.q });
      AudioService.updateEqBand(4, this.lowShelf.frequency, this.lowShelf.gain, this.lowShelf.q);
    },

    // Compressor updates
    updateCompressorThreshold() {
      this.updateCompressorParam({ param: 'threshold', value: this.compressor.threshold });
      AudioService.updateCompressor(
        this.compressor.threshold,
        this.compressor.ratio,
        this.compressor.attack,
        this.compressor.release
      );
    },
    updateCompressorRatio() {
      this.updateCompressorParam({ param: 'ratio', value: this.compressor.ratio });
      AudioService.updateCompressor(
        this.compressor.threshold,
        this.compressor.ratio,
        this.compressor.attack,
        this.compressor.release
      );
    },
    updateCompressorAttack() {
      this.updateCompressorParam({ param: 'attack', value: this.compressor.attack });
      AudioService.updateCompressor(
        this.compressor.threshold,
        this.compressor.ratio,
        this.compressor.attack,
        this.compressor.release
      );
    },
    updateCompressorRelease() {
      this.updateCompressorParam({ param: 'release', value: this.compressor.release });
      AudioService.updateCompressor(
        this.compressor.threshold,
        this.compressor.ratio,
        this.compressor.attack,
        this.compressor.release
      );
    },
    updateCompressorMakeup() {
      this.updateCompressorParam({ param: 'makeupGain', value: this.compressor.makeupGain });
      AudioService.updateMakeupGain(this.compressor.makeupGain);
    },

    resetToDefault() {
      this.resetToDefaultAction();
      this.loadSettings();

      // Update all filters in AudioService
      if (AudioService.isWebAudioInitialized) {
        AudioService.updateEqBand(0, this.highShelf.frequency, this.highShelf.gain, this.highShelf.q);
        AudioService.updateEqBand(1, this.mid3.frequency, this.mid3.gain, this.mid3.q);
        AudioService.updateEqBand(2, this.mid2.frequency, this.mid2.gain, this.mid2.q);
        AudioService.updateEqBand(3, this.mid1.frequency, this.mid1.gain, this.mid1.q);
        AudioService.updateEqBand(4, this.lowShelf.frequency, this.lowShelf.gain, this.lowShelf.q);
        AudioService.updateCompressor(
          this.compressor.threshold,
          this.compressor.ratio,
          this.compressor.attack,
          this.compressor.release
        );
        AudioService.updateMakeupGain(this.compressor.makeupGain);

        // Re-apply enabled states to ensure effects are properly bypassed if disabled
        AudioService.setCompressorEnabled(this.compressorEnabled);
        AudioService.setEqEnabled(this.eqEnabled);
      }
    },

    // Individual EQ band resets
    resetHighShelf() {
      this.highShelf = { frequency: 8000, gain: 0, q: 0.7 };
      this.updateEqBand({ band: 'highShelf', property: 'frequency', value: 8000 });
      this.updateEqBand({ band: 'highShelf', property: 'gain', value: 0 });
      this.updateEqBand({ band: 'highShelf', property: 'q', value: 0.7 });
      if (AudioService.isWebAudioInitialized) {
        AudioService.updateEqBand(0, 8000, 0, 0.7);
      }
    },

    resetMid3() {
      this.mid3 = { frequency: 4000, gain: 0, q: 1.0 };
      this.updateEqBand({ band: 'mid3', property: 'frequency', value: 4000 });
      this.updateEqBand({ band: 'mid3', property: 'gain', value: 0 });
      this.updateEqBand({ band: 'mid3', property: 'q', value: 1.0 });
      if (AudioService.isWebAudioInitialized) {
        AudioService.updateEqBand(1, 4000, 0, 1.0);
      }
    },

    resetMid2() {
      this.mid2 = { frequency: 1000, gain: 0, q: 1.0 };
      this.updateEqBand({ band: 'mid2', property: 'frequency', value: 1000 });
      this.updateEqBand({ band: 'mid2', property: 'gain', value: 0 });
      this.updateEqBand({ band: 'mid2', property: 'q', value: 1.0 });
      if (AudioService.isWebAudioInitialized) {
        AudioService.updateEqBand(2, 1000, 0, 1.0);
      }
    },

    resetMid1() {
      this.mid1 = { frequency: 250, gain: 0, q: 1.0 };
      this.updateEqBand({ band: 'mid1', property: 'frequency', value: 250 });
      this.updateEqBand({ band: 'mid1', property: 'gain', value: 0 });
      this.updateEqBand({ band: 'mid1', property: 'q', value: 1.0 });
      if (AudioService.isWebAudioInitialized) {
        AudioService.updateEqBand(3, 250, 0, 1.0);
      }
    },

    resetLowShelf() {
      this.lowShelf = { frequency: 80, gain: 0, q: 0.7 };
      this.updateEqBand({ band: 'lowShelf', property: 'frequency', value: 80 });
      this.updateEqBand({ band: 'lowShelf', property: 'gain', value: 0 });
      this.updateEqBand({ band: 'lowShelf', property: 'q', value: 0.7 });
      if (AudioService.isWebAudioInitialized) {
        AudioService.updateEqBand(4, 80, 0, 0.7);
      }
    },

    // Equalizer reset (all bands)
    resetEqualizer() {
      this.resetHighShelf();
      this.resetMid3();
      this.resetMid2();
      this.resetMid1();
      this.resetLowShelf();
    },

    // Compressor reset
    resetCompressor() {
      this.compressor = {
        threshold: -24,
        ratio: 3.5,
        attack: 30,
        release: 100,
        makeupGain: 0
      };
      this.updateCompressorParam({ param: 'threshold', value: -24 });
      this.updateCompressorParam({ param: 'ratio', value: 3.5 });
      this.updateCompressorParam({ param: 'attack', value: 30 });
      this.updateCompressorParam({ param: 'release', value: 100 });
      this.updateCompressorParam({ param: 'makeupGain', value: 0 });
      if (AudioService.isWebAudioInitialized) {
        AudioService.updateCompressor(-24, 3.5, 30, 100);
        AudioService.updateMakeupGain(0);
      }
    },

    // Gain reduction meter animation (desktop: live node; iOS: estimated envelope)
    startMeterAnimation() {
      const updateMeter = () => {
        this.gainReduction = AudioService.getCompressorReduction();
        this.meterAnimationId = requestAnimationFrame(updateMeter);
      };
      updateMeter();
    },

    stopMeterAnimation() {
      if (this.meterAnimationId) {
        cancelAnimationFrame(this.meterAnimationId);
        this.meterAnimationId = null;
      }
    }
  },

  beforeDestroy() {
    this.stopMeterAnimation();
  }
};
</script>

<style scoped>
.highestZIndex {
  z-index: 999999999 !important;
}

/* Card with reduced padding */
.effects-card {
  padding: 5px;
}

/* Sticky header with centered title */
.effects-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
  background: inherit;
  padding: 8px 0;
  margin: -5px -5px 0 -5px;
  width: calc(100% + 10px);
}

.effects-title {
  font-size: 1.25rem;
  font-weight: 500;
  text-align: center;
  flex: 1;
  letter-spacing: 1px;
}

/* Content area - override Vuetify's default v-card-text padding */
.effects-content {
  padding: 0 5px 0 5px !important;
  /* iOS Safari scroll fixes */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  /* Hide scrollbar in all browsers while keeping scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}

/* Hide scrollbar for Chrome, Safari, and newer Edge */
.effects-content::-webkit-scrollbar {
  display: none;
}

/* Section headers - smaller than main title */
.section-header {
  padding: 12px 0 6px 0;
}

/* Smaller section titles - bold for visibility */
.compressor-header .v-subheader,
.eq-header .v-subheader {
  font-size: 0.9rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
}

.eq-header {
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.compressor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.auto-makeup-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.auto-makeup-label {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-switch {
  margin: 0 !important;
  padding: 0 !important;
}

/* Section content */
.section-content {
  padding: 0;
  transition: opacity 0.2s ease;
}

/* Disabled section styling */
.section-disabled {
  opacity: 0.4;
  pointer-events: none;
}

/* Bottom padding for iOS Safari to ensure all content is accessible */
.section-content:last-of-type {
  padding-bottom: 60px;
}

.eq-band {
  margin-bottom: 16px;
}

.band-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.band-title {
  font-weight: 500;
  font-size: 14px;
  color: var(--v-secondary-base);
}

.slider-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.slider-label {
  min-width: 60px;
  font-size: 13px;
  color: var(--v-secondary-base);
}

.slider-value {
  min-width: 80px;
  text-align: right;
  font-size: 13px;
  color: var(--v-primary-base);
  font-family: monospace;
}

/* Gain Reduction Meter */
.gain-reduction-meter-container {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px 0;
}

.offline-fx-note {
  margin-bottom: 16px;
  padding: 8px 0;
  font-size: 12px;
  opacity: 0.7;
  font-style: italic;
}

.meter-label {
  min-width: 60px;
  font-size: 13px;
  color: var(--v-secondary-base);
  font-weight: 500;
}

.meter-track {
  flex: 1;
  height: 20px;
  background: #d0d0d0;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  border: 1px solid #bbb;
}

.meter-fill {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  background: #505050;
  transition: width 50ms ease-out;
  border-radius: 1px;
}

.meter-scale {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  pointer-events: none;
}

.meter-mark {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 9px;
  color: rgba(0, 0, 0, 0.5);
  font-family: monospace;
}

.meter-value {
  min-width: 80px;
  text-align: right;
  font-size: 13px;
  color: var(--v-primary-base);
  font-family: monospace;
}

/* Make dialog scrollable on smaller screens */
@media only screen and (max-height: 800px) {
  .effects-content {
    max-height: 500px;
    overflow-y: auto;
    /* Hide scrollbar in all browsers while keeping scroll functionality */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }

  /* Hide scrollbar for Chrome, Safari, and newer Edge */
  .effects-content::-webkit-scrollbar {
    display: none;
  }
}

/* Adjustments for smaller screens */
@media only screen and (max-width: 700px) {
  .effects-title {
    font-size: 1.1rem;
  }

  .compressor-header .v-subheader,
  .eq-header {
    font-size: 0.85rem !important;
  }

  .eq-header {
    padding-top: 14px;
  }

  .slider-label {
    min-width: 50px;
    font-size: 12px;
  }

  .slider-value {
    min-width: 70px;
    font-size: 12px;
  }

  .meter-label {
    min-width: 50px;
    font-size: 12px;
  }

  .meter-track {
    height: 16px;
  }

  .meter-mark {
    font-size: 8px;
  }

  .meter-value {
    min-width: 70px;
    font-size: 12px;
  }
}

/* Mobile adjustments for compressor header - fits down to 320px */
@media only screen and (max-width: 480px) {
  .section-label {
    font-size: 0.7rem !important;
  }

  .auto-makeup-label {
    font-size: 9px;
  }

  .section-title-row {
    gap: 2px;
  }

  .header-controls {
    gap: 0px;
  }

  .section-switch,
  .auto-makeup-switch {
    transform: scale(0.85);
    transform-origin: center;
  }

  .section-title-row .v-btn--icon.v-size--x-small {
    width: 20px !important;
    height: 20px !important;
  }
}

/* Extra small screens - 320px and below */
@media only screen and (max-width: 360px) {
  .section-label {
    font-size: 0.65rem !important;
  }

  .auto-makeup-label {
    font-size: 8px;
  }

  .section-title-row {
    gap: 1px;
  }

  .header-controls {
    gap: 0px;
  }

  .section-header {
    padding: 6px 0 2px 0;
  }

  .section-switch,
  .auto-makeup-switch {
    transform: scale(0.75);
    transform-origin: center;
  }

  .section-title-row .v-btn--icon.v-size--x-small {
    width: 18px !important;
    height: 18px !important;
  }

  .section-title-row .v-btn--icon.v-size--x-small .v-icon {
    font-size: 14px !important;
  }
}
</style>

<!-- Non-scoped styles for dialog container -->
<style>
/* Hide scrollbars on effects dialog specifically */
.v-dialog:has(.effects-card) {
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE/Edge */
}

.v-dialog:has(.effects-card)::-webkit-scrollbar {
  display: none !important; /* Chrome, Safari, Edge */
}

/* Reduce dialog margins on screens 700px and below */
@media only screen and (max-width: 700px) {
  .v-dialog.highestZIndex {
    margin: 5px !important;
  }
}

/* Dark mode meter colors */
.theme--dark .meter-track {
  background: #1a1a1a;
  border-color: #333;
}

.theme--dark .meter-fill {
  background: #b0b0b0;
}

.theme--dark .meter-mark {
  color: rgba(255, 255, 255, 0.5);
}
</style>
